/**
 * Utilitários para sanitização de inputs e proteção contra XSS
 */

/**
 * Escapa caracteres HTML perigosos
 * @param {string} str - String a ser sanitizada
 * @returns {string} String sanitizada
 */
export const escapeHtml = (str) => {
  if (typeof str !== "string") return str;

  const map = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
    "/": "&#x2F;",
  };

  return str.replace(/[&<>"'/]/g, (char) => map[char]);
};

/**
 * Remove tags HTML de uma string
 * @param {string} str - String que pode conter HTML
 * @returns {string} String sem tags HTML
 */
export const stripHtml = (str) => {
  if (typeof str !== "string") return str;
  return str.replace(/<[^>]*>/g, "");
};

/**
 * Sanitiza uma string removendo caracteres perigosos e tags HTML
 * @param {string} str - String a ser sanitizada
 * @param {object} options - Opções de sanitização
 * @param {boolean} options.allowHtml - Se deve permitir HTML (padrão: false)
 * @param {boolean} options.trim - Se deve remover espaços (padrão: true)
 * @returns {string} String sanitizada
 */
export const sanitizeString = (str, options = {}) => {
  if (typeof str !== "string") return str;

  const { allowHtml = false, trim = true } = options;
  let sanitized = str;

  if (trim) {
    sanitized = sanitized.trim();
  }

  if (!allowHtml) {
    sanitized = stripHtml(sanitized);
    sanitized = escapeHtml(sanitized);
  }

  return sanitized;
};

/**
 * Sanitiza um objeto recursivamente
 * @param {object} obj - Objeto a ser sanitizado
 * @param {object} options - Opções de sanitização
 * @returns {object} Objeto sanitizado
 */
export const sanitizeObject = (obj, options = {}) => {
  if (obj === null || obj === undefined) return obj;

  if (typeof obj === "string") {
    return sanitizeString(obj, options);
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => sanitizeObject(item, options));
  }

  if (typeof obj === "object") {
    const sanitized = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        sanitized[key] = sanitizeObject(obj[key], options);
      }
    }
    return sanitized;
  }

  return obj;
};

/**
 * Valida e sanitiza um email
 * @param {string} email - Email a ser validado
 * @returns {string|null} Email sanitizado ou null se inválido
 */
export const sanitizeEmail = (email) => {
  if (typeof email !== "string") return null;

  const sanitized = email.trim().toLowerCase();
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  return emailRegex.test(sanitized) ? sanitized : null;
};

/**
 * Valida e sanitiza uma URL
 * @param {string} url - URL a ser validada
 * @returns {string|null} URL sanitizada ou null se inválida
 */
export const sanitizeUrl = (url) => {
  if (typeof url !== "string") return null;

  try {
    const urlObj = new URL(url);
    return urlObj.href;
  } catch {
    return null;
  }
};

